import {useEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent} from 'react';
import styles from './lab.module.css';
import type {Line, CompletionResult} from '../shell';

/** A shell Line plus the terminal's own kinds: the echoed command row and its pending marker. */
interface TermLine {
    text: string;
    kind: Line['kind'] | 'cmd';
    promptCwd?: string;
    pending?: boolean;
}

interface TerminalProps {
    cwd: string;
    onRun(input: string): Promise<Line[] | undefined> | Line[] | undefined;
    onComplete?(input: string, caret: number | undefined): CompletionResult | null;
    chromeless?: boolean;
}

const WELCOME_LINE: TermLine = {text: 'CALM learning lab — type `help` to get started.', kind: 'dim'};

function shortPath(cwd: string): string {
    if (cwd === '/workspace') {
        return '~';
    }
    return cwd.startsWith('/workspace/') ? '~' + cwd.slice('/workspace'.length) : cwd;
}

function Prompt({cwd}: {cwd: string}) {
    return (
        <>
            <span className={styles.promptUser}>learner@calm-lab</span>
            <span className={styles.promptSep}>:</span>
            <span className={styles.promptPath}>{shortPath(cwd)}</span>
            <span className={styles.promptSep}>$</span>
        </>
    );
}

export default function Terminal({cwd, onRun, onComplete, chromeless = false}: TerminalProps) {
    const [lines, setLines] = useState<TermLine[]>([WELCOME_LINE]);
    const [input, setInput] = useState('');
    const [history, setHistory] = useState<string[]>([]);
    const [busy, setBusy] = useState(false);
    const historyPos = useRef(-1);
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const paneRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [lines]);

    // The input is disabled while a command runs, which drops focus to the body — take it back
    // once the command finishes. Guarded so the terminal does not grab focus on first mount, nor
    // pull the caret out of the editor if the learner moved there while the command ran.
    const wasBusy = useRef(false);
    useEffect(() => {
        if (wasBusy.current && !busy) {
            const active = document.activeElement;
            const focusIsLoose = !active || active === document.body;
            if (focusIsLoose || paneRef.current?.contains(active)) {
                inputRef.current?.focus();
            }
        }
        wasBusy.current = busy;
    }, [busy]);

    // Reset lesson remounts the terminal; a pasted batch must not keep running into the new session.
    // Set on mount too: StrictMode unmounts and remounts once in development.
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const execute = async (value: string) => {
        const promptCwd = cwd;
        // Echo the command and a placeholder straight away — validation is async now, so the
        // result can be a tick or two behind the keystroke.
        setLines((prev) => [
            ...prev,
            {text: value, kind: 'cmd', promptCwd},
            {text: '…', kind: 'dim', pending: true},
        ]);
        if (value.trim()) {
            setHistory((prev) => [...prev, value]);
        }
        historyPos.current = -1;
        setBusy(true);
        let result: Line[] = [];
        try {
            result = (await onRun(value)) || [];
        } catch (error) {
            // Without this the `…` placeholder would stay on screen forever.
            result = [{text: 'error: ' + ((error as {message?: string})?.message ?? String(error)), kind: 'err'}];
        } finally {
            setBusy(false);
        }
        if (result.some((line) => line.kind === 'clear')) {
            setLines([]);
        } else {
            setLines((prev) => [...prev.filter((line) => !line.pending), ...result]);
        }
    };

    const submit = () => {
        const value = input;
        setInput('');
        void execute(value);
    };

    // A multi-line paste runs each complete line in order, as a shell does; the text after the
    // last newline stays in the prompt. A single line pastes normally.
    const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
        const text = event.clipboardData.getData('text');
        if (!/\r?\n/.test(text)) {
            return;
        }
        event.preventDefault();
        if (busy) {
            return;
        }
        const el = event.currentTarget;
        const start = el.selectionStart ?? input.length;
        const end = el.selectionEnd ?? start;
        const lines = (input.slice(0, start) + text + input.slice(end)).split(/\r?\n/);
        const rest = lines.pop() ?? '';
        setInput(rest);
        void (async () => {
            for (const line of lines) {
                if (!mounted.current) {
                    return;
                }
                await execute(line);
            }
        })();
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (busy) {
                return;
            }
            submit();
        } else if (event.key === 'Tab') {
            event.preventDefault();
            if (!onComplete) {
                return;
            }
            const el = event.target as HTMLInputElement;
            const result = onComplete(input, el.selectionStart ?? input.length);
            if (!result) {
                return;
            }
            if ('value' in result) {
                setInput(result.value);
                const caret = result.caret;
                requestAnimationFrame(() => el.setSelectionRange(caret, caret));
            } else if (result.candidates?.length) {
                setLines((prev) => [
                    ...prev,
                    {text: result.candidates.join('  '), kind: 'dim'},
                ]);
            }
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            if (history.length) {
                const next =
                    historyPos.current === -1
                        ? history.length - 1
                        : Math.max(0, historyPos.current - 1);
                historyPos.current = next;
                setInput(history[next]);
            }
        } else if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (historyPos.current !== -1) {
                const next = historyPos.current + 1;
                if (next >= history.length) {
                    historyPos.current = -1;
                    setInput('');
                } else {
                    historyPos.current = next;
                    setInput(history[next]);
                }
            }
        }
    };

    const focusInput = () => {
        if (typeof window !== 'undefined' && window.getSelection?.()?.toString()) {
            return;
        }
        inputRef.current?.focus();
    };

    return (
        <div
            className={chromeless ? styles.paneFill : styles.terminalPane}
            ref={paneRef}
            onClick={focusInput}>
            {!chromeless && (
                <div className={styles.paneHeader}>
                    <span className={styles.paneDots} aria-hidden="true">
                        <i /><i /><i />
                    </span>
                    terminal
                </div>
            )}
            <div className={styles.termScroll} ref={scrollRef}>
                {lines.map((line, index) =>
                    line.kind === 'cmd' ? (
                        <div className={styles.termLine} key={index}>
                            <Prompt cwd={line.promptCwd!} />
                            <span className={styles.lineCmd}> {line.text}</span>
                        </div>
                    ) : (
                        <div
                            className={`${styles.termLine} ${styles[lineClass(line.kind)]}`}
                            key={index}>
                            {line.text || ' '}
                        </div>
                    ),
                )}
                <div className={styles.promptRow}>
                    <Prompt cwd={cwd} />
                    <input
                        ref={inputRef}
                        className={styles.termInput}
                        aria-label="Terminal input"
                        value={input}
                        onChange={(event) => setInput(event.target.value)}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        disabled={busy}
                        spellCheck={false}
                        autoComplete="off"
                        autoCapitalize="off"
                        autoCorrect="off"
                    />
                </div>
            </div>
        </div>
    );
}

function lineClass(kind: TermLine['kind']): string {
    switch (kind) {
        case 'ok':
            return 'lineOk';
        case 'err':
            return 'lineErr';
        case 'dim':
            return 'lineDim';
        default:
            return 'lineOut';
    }
}
